import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Shirt, X } from 'lucide-react'
import { useFloatingAssistantState } from '@/store/selectors'
import { loadCreamCloudPet, type PetState } from './creamCloudPet'
import WardrobeModal from './WardrobeModal'
import './floatingAssistant.css'

function routeMessage(pathname: string, state: ReturnType<typeof useFloatingAssistantState>) {
  if (pathname === '/' || pathname === '/dashboard') {
    return '学习节奏由你安排；完成任意一项，都会计入本月累计。'
  }
  if (pathname === '/learning-path') return '本单元层级已经锁定；过程练习用于发现薄弱项，结业后再决定下一阶段。'
  if (pathname === '/practice') return '本次练习会记录为过程证据，用于发现薄弱项和调整下一阶段题量。'
  if (pathname === '/assessment') return '先独立作答。完成后我再陪你看结果。'
  if (pathname === '/me') return '这里可以查看动态能力档案，也能打开我的衣橱。'
  if (pathname === '/pretest') return '前测用来建立起点，不会给你贴上永久标签。'
  return state.student.streak >= 3 ? `已经连续学习 ${state.student.streak} 天，保持自己的节奏。` : null
}

export default function FloatingAssistant() {
  const location = useLocation()
  const assistantState = useFloatingAssistantState()
  const [loaded, setLoaded] = useState(false)
  const [wardrobeOpen, setWardrobeOpen] = useState(false)
  const [bubbleOpen, setBubbleOpen] = useState(false)
  const [petState, setPetState] = useState<PetState>('idle')
  const message = useMemo(() => routeMessage(location.pathname, assistantState), [location.pathname, assistantState])
  const focused = ['/pretest', '/assessment', '/classroom'].includes(location.pathname) || location.pathname.startsWith('/lesson/')
  // Keep the homepage focused on its single task CTA; dense indexes also stay unobstructed.
  const hidden = location.pathname === '/'
    || location.pathname === '/dashboard'
    || location.pathname === '/learning-path'
    || location.pathname === '/courses'

  useEffect(() => {
    loadCreamCloudPet().then(() => setLoaded(true)).catch(() => setLoaded(false))
  }, [])

  useEffect(() => {
    setBubbleOpen(false)
  }, [location.pathname, location.search])

  const openWardrobe = useCallback(() => {
    setPetState('jumping')
    setBubbleOpen(false)
    window.setTimeout(() => {
      setWardrobeOpen(true)
      setPetState('idle')
    }, 260)
  }, [])

  if (!loaded || hidden || focused) return null

  return (
    <>
      <div className="floating-pet">
        {bubbleOpen && (
          <section className="floating-pet-bubble" aria-label="奶油云团助手">
            <button type="button" className="floating-pet-bubble-close" onClick={() => setBubbleOpen(false)} aria-label="关闭助手">
              <X aria-hidden="true" />
            </button>
            <p>{message ?? '需要调整节奏时，可以从学习路径或 AI 老师继续。'}</p>
            <button type="button" className="floating-pet-wardrobe" onClick={openWardrobe}>
              <Shirt aria-hidden="true" />
              打开衣橱
            </button>
          </section>
        )}
        <button
          className="floating-pet-button"
          type="button"
          aria-label="打开奶油云团助手"
          aria-expanded={bubbleOpen}
          title="奶油云团助手"
          onClick={() => setBubbleOpen((open) => !open)}
        >
          <cream-cloud-pet state={petState} size="56" interactive outfit={assistantState.currentOutfit} />
        </button>
      </div>
      {wardrobeOpen && <WardrobeModal onClose={() => setWardrobeOpen(false)} />}
    </>
  )
}
